import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { Typography } from '@mui/material';
import { getNodesAvailable } from 'lib/api/decisionEngine';
import CustomButton from 'lib/designSystem/Button';
import {
  Node,
  RevisionObjective,
} from 'pages/Settings/Components/Risk/Components/DecisionEngines/components/Create/types';
import React, { useState } from 'react';
import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';

import RangesAssign from '../newAssignation/RangesAssignation';

type Props = {
  openEditNode: boolean;
  setOpenEditNode: (open: boolean) => void;
  editingNode: Node;
  setEditingNode: (node: Node) => void;
  decisionNodesAssignation: Node[];
  setDecisionNodesAssignation: (nodes: Node[]) => void;
  revisionObjective: RevisionObjective;
};

const EditAssignation: React.FC<Props> = ({
  openEditNode,
  setOpenEditNode,
  editingNode,
  setEditingNode,
  decisionNodesAssignation,
  setDecisionNodesAssignation,
  revisionObjective,
}) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { data } = getNodesAvailable(dispatch, revisionObjective);
  const decisionNodes = data?.decisionNodes || [];
  const categorizedNodes = data?.categorizedNodes || {};
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [operation, setOperation] = useState('gt');

  const saveNode = () => {
    const nodes = [...decisionNodesAssignation];
    nodes[nodes.findIndex((x) => x.id === editingNode.id)] = editingNode;
    setDecisionNodesAssignation(nodes);
    setOpenEditNode(false);
  };

  const handleChangeCategory = (e: SelectChangeEvent<string>) => {
    setSelectedCategory(e.target.value);
  };
  const setUpRangeForBoolean = () => {
    const ranges = [
      {
        value: '0',
        decisionNodeId: editingNode.id,
        nodeId: 0,
      },
      {
        value: '1',
        decisionNodeId: editingNode.id,
        nodeId: 1,
      },
    ];
    return ranges;
  };

  const handleChangeOrigin = (e: SelectChangeEvent<string>) => {
    const { value } = e.target;
    const [originNodeIdStr, metricIdStr] = String(value).split('-');
    const originNodeId = Number(originNodeIdStr);
    const node = decisionNodes.find(
      (x) =>
        x.id === originNodeId &&
        String(x.metricId ?? '') === (metricIdStr ?? ''),
    );
    return setEditingNode({
      ...editingNode,
      originNodeId,
      value: '',
      type: node?.type || null,
      operation: node?.operation || 'gt',
      description: node?.description || '',
      name: node?.name || '',
      id: editingNode.id,
      ranges: node?.type === 'boolean' ? setUpRangeForBoolean() : [],
      metricId: node?.metricId ?? null,
    });
  };

  const availableNodeForSelection = () => {
    const selectedPairs = decisionNodesAssignation
      .filter((n) => n.originNodeId != null && n.id !== editingNode.id)
      .map((n) => `${n.originNodeId}-${n.metricId ?? ''}`);
    return decisionNodes.filter((x) => {
      if (x.type === 'final') return false;
      const pair = `${x.id}-${x.metricId ?? ''}`;
      return (
        !selectedPairs.includes(pair) &&
        x.category === selectedCategory &&
        Number(x.subDecisionEngineId) !== Number(id)
      );
    });
  };

  const availableFinalNodeForSelection = () => {
    return decisionNodes.filter((x) => x.type === 'final');
  };

  const addRange = () => {
    const range = {
      value: '',
      decisionNodeId: editingNode.id,
    };
    setEditingNode({ ...editingNode, ranges: [...editingNode.ranges, range] });
  };

  const handleChangeRange = (e: SelectChangeEvent<number>, index: number) => {
    const { value } = e.target;
    const { name } = e.target;
    const ranges = editingNode.ranges.map((x, i) => {
      if (i === index) return { ...x, [name]: value };
      return x;
    });
    setEditingNode({ ...editingNode, ranges });
  };

  const deleteRange = (index: number) => {
    const ranges = editingNode.ranges.filter((x, i) => i !== index);
    ranges[ranges.length - 1].max = null;
    setEditingNode({ ...editingNode, ranges });
  };

  useEffect(() => {
    const category = Object.keys(categorizedNodes).find((x) =>
      categorizedNodes[x].some((y) => y.id === editingNode.originNodeId),
    );
    if (category) setSelectedCategory(category);
    setOperation(editingNode.operation);
  }, [editingNode]);

  return (
    <Dialog
      maxWidth="md"
      open={openEditNode}
      onClose={() => setOpenEditNode(false)}
    >
      <DialogTitle>Editar evaluación</DialogTitle>
      <DialogContent style={{ width: '500px' }}>
        <Typography variant="body1" gutterBottom>
          Acá podrás definir una nueva evaluación dentro de la matriz.
        </Typography>
        <Typography variant="body1" gutterBottom>
          <b>Evaluación a agregar</b>
        </Typography>
        <div style={{ padding: '10px 0px' }}>
          <FormControl fullWidth variant="outlined">
            <InputLabel id="filter-label">Categorías</InputLabel>
            <Select
              labelId="filter-label"
              value={selectedCategory}
              onChange={handleChangeCategory}
              label="Categorías"
            >
              {Object.keys(categorizedNodes).map((x, index) => (
                <MenuItem key={index} value={x}>
                  {x}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: '10px 0px' }}>
          <FormControl
            fullWidth
            variant="outlined"
            disabled={selectedCategory === ''}
          >
            <InputLabel id="filter-label">Evaluaciones disponibles</InputLabel>
            <Select
              labelId="filter-label"
              id="Filtrar por estado"
              value={
                editingNode.originNodeId != null
                  ? `${editingNode.originNodeId}-${editingNode.metricId ?? ''}`
                  : ''
              }
              onChange={handleChangeOrigin}
              label="Evaluaciones disponibles"
            >
              {availableNodeForSelection().map((x, index) => (
                <MenuItem key={index} value={`${x.id}-${x.metricId ?? ''}`}>
                  {x.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </div>
        {editingNode.originNodeId && (
          <div style={{ padding: '20ox' }}>
            <Typography variant="body1" gutterBottom>
              {editingNode.description}
            </Typography>
          </div>
        )}
        {editingNode.type !== 'decisionEngine' && editingNode.originNodeId && (
          <RangesAssign
            newNode={editingNode}
            setNewNode={setEditingNode}
            addRange={addRange}
            handleChangeRange={handleChangeRange}
            deleteRange={deleteRange}
            availableFinalNodeForSelection={availableFinalNodeForSelection}
            operation={operation}
            setOperation={setOperation}
          />
        )}
      </DialogContent>
      <DialogActions>
        <CustomButton
          onClick={() => setOpenEditNode(false)}
          color="secondary"
          variant="contained"
        >
          Cancelar
        </CustomButton>
        <CustomButton
          onClick={() => saveNode()}
          color="primary"
          variant="contained"
        >
          Confirmar
        </CustomButton>
      </DialogActions>
    </Dialog>
  );
};

export default EditAssignation;
